import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { signOut } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import DashboardShell from './DashboardShell';
import bestLogo from '../assets/logo-best.jpeg';
import './TeacherDashboard.css';

const SHEET_CSV_URL =
  'https://docs.google.com/spreadsheets/d/e/2PACX-1vSmALqApJN_tNwe_Pp5blp01BQLxoF0z5WZLb0L7kMkhSQB2CtKtyuOe-4Op5Ya7K_OeVZRGZhVxE8N/pub?output=csv';
const TEACHER_ATTENDANCE_SESSION_TARGET = 10;
const sanitizePhoneNumber = (value) => String(value || '').replace(/[^\d]/g, '');
const TEACHER_REFLECTION_FORM_URL = process.env.REACT_APP_TEACHER_REFLECTION_FORM_URL || '';

const normalizeMatchText = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9 ]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getRowField = (row, labelStart) => {
  const foundKey = Object.keys(row || {}).find((key) => (
    String(key || '').trim().toLowerCase().startsWith(String(labelStart || '').trim().toLowerCase())
  ));

  return foundKey ? row[foundKey] : '';
};

const parseAttendanceFraction = (value) => {
  const match = String(value || '').match(/(\d+)\s*\/\s*(\d+)/);
  if (!match) return { present: 0, total: 0, percent: null };

  const present = Number(match[1]);
  const total = Number(match[2]);
  return {
    present,
    total,
    percent: total ? Math.round((present / total) * 100) : null,
  };
};

const getSheetTimestampMs = (value) => {
  if (!value) return 0;

  const raw = String(value).trim();
  const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);

  if (match) {
    const [, month, day, year, hour = '0', minute = '0', second = '0'] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const buildISODate = (yyyy, mm, dd) => {
  const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (
    date.getFullYear() !== Number(yyyy) ||
    date.getMonth() !== Number(mm) - 1 ||
    date.getDate() !== Number(dd)
  ) {
    return '';
  }

  return `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
};

const parseSheetDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?)?$/);
  if (match) {
    const [, first, second, yyyy] = match;
    const firstNumber = Number(first);
    const secondNumber = Number(second);

    if (firstNumber > 12) return buildISODate(yyyy, second, first);
    if (secondNumber > 12) return buildISODate(yyyy, first, second);
    return buildISODate(yyyy, first, second);
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
};

const normalizeSubject = (value) => {
  const raw = normalizeMatchText(value);
  if (!raw) return '';
  if (raw.includes('bahasa inggeris') || raw === 'bi' || raw.includes(' bi ')) return 'bi';
  if (raw.includes('bahasa melayu') || raw === 'bm' || raw.includes(' bm ')) return 'bm';
  if (raw.includes('matematik') || raw.includes('math') || raw === 'mt') return 'math';
  if (raw.includes('sejarah') || raw.includes('sej')) return 'sejarah';
  return raw;
};

const getAttendanceRecordKey = (date, subject) => `${String(date || '').trim()}|${normalizeSubject(subject)}`;

const isTeacherAttendanceRow = (row, userData, teacherAliases = []) => {
  const teacherNames = teacherAliases
    .map((name) => normalizeMatchText(name))
    .filter((name) => name.length >= 3);
  const userSubject = normalizeSubject(userData?.subject);

  const rowTeacher = normalizeMatchText([
    row['Nama Penuh (Huruf Besar)'],
    row['Nama Penuh'],
    row['Nama'],
  ].filter(Boolean).join(' '));
  const rowSubject = normalizeSubject(row['Guru mata pelajaran']);
  const teacherMatches = teacherNames.some((teacherName) => {
    const firstName = teacherName.split(' ')[0] || '';
    return rowTeacher.includes(teacherName) || (firstName.length >= 4 && rowTeacher.includes(firstName));
  });
  const subjectMatches = userSubject ? rowSubject === userSubject || rowSubject.includes(userSubject) : true;

  return teacherMatches && subjectMatches;
};

const TeacherDashboard = () => {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [loadingAttendance, setLoadingAttendance] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [upcomingSchedules, setUpcomingSchedules] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [toast, setToast] = useState({ show: false, type: 'ok', msg: '' });
  const [activeTeacherView, setActiveTeacherView] = useState('overview');

  const showToast = (type, msg) => {
    setToast({ show: true, type, msg });
    window.setTimeout(() => {
      setToast({ show: false, type: 'ok', msg: '' });
    }, 2600);
  };

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const supportNumber = useMemo(
    () => sanitizePhoneNumber(process.env.REACT_APP_SUPPORT_WHATSAPP_NUMBER),
    []
  );

  const formatMalayDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(`${isoDate}T00:00:00`);
    const days = ['Ahad', 'Isnin', 'Selasa', 'Rabu', 'Khamis', 'Jumaat', 'Sabtu'];
    const months = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogos', 'Sep', 'Okt', 'Nov', 'Dis'];
    return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hh, mm] = timeStr.split(':');
    const hour = Number(hh);
    if (Number.isNaN(hour)) return timeStr;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${displayHour}:${mm} ${ampm}`;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Selamat pagi';
    if (hour < 17) return 'Selamat tengah hari';
    return 'Selamat petang';
  };

  const getTypeLabel = (type) => {
    if (!type) return 'Aktiviti';
    if (type === 'PlayAndTalk') return 'Play & Talk';
    if (type === 'IVC') return 'Kelas IVC';
    if (type === 'Workshop') return 'Bengkel';
    if (type === 'Quiz') return 'Kuiz';
    if (type === 'Seminar') return 'Seminar';
    return type;
  };

  const getAttendanceTone = (rate) => {
    if (rate === null || rate === undefined || Number.isNaN(Number(rate))) return 'pending';
    const value = Number(rate);
    if (value >= 90) return 'good';
    if (value >= 75) return 'warn';
    return 'bad';
  };

  const getAttendanceLabel = (rate) => {
    if (rate === null || rate === undefined || Number.isNaN(Number(rate))) return 'Belum lengkap';
    return `${Math.round(Number(rate))}%`;
  };

  const firstName = useMemo(() => {
    const name = userData?.fullName || '';
    return name.trim().split(' ')[0] || 'Cikgu';
  }, [userData]);

  const totalClasses = schedules.length;
  const todayClasses = useMemo(
    () => schedules.filter((schedule) => schedule.date === todayISO).length,
    [schedules, todayISO]
  );
  const upcomingCount = useMemo(
    () => schedules.filter((schedule) => schedule.date && schedule.date >= todayISO).length,
    [schedules, todayISO]
  );
  const nextSchedule = upcomingSchedules[0] || null;
  const nextScheduleLabel = nextSchedule
    ? `${formatMalayDate(nextSchedule.date)} | ${formatTime(nextSchedule.startTime)}${nextSchedule.endTime ? ` - ${formatTime(nextSchedule.endTime)}` : ''}`
    : '';
  const teacherAttendanceAliases = useMemo(() => {
    return [
      userData?.fullName,
      ...schedules.map((schedule) => schedule.teacher),
    ]
      .map((name) => String(name || '').trim())
      .filter(Boolean)
      .filter((name, index, list) => list.findIndex((item) => normalizeMatchText(item) === normalizeMatchText(name)) === index);
  }, [schedules, userData?.fullName]);
  const attendanceSummary = useMemo(() => {
    const teacherRows = attendanceRows
      .filter((row) => isTeacherAttendanceRow(row, userData, teacherAttendanceAliases))
      .map((row) => {
        const attendanceText = row['Jumlah Kehadiran Pelajar (cnth: 23/25)'];
        const attendance = parseAttendanceFraction(attendanceText);

        return {
          ...attendance,
          attendanceText,
          timestampMs: getSheetTimestampMs(row.Timestamp),
          subject: row['Guru mata pelajaran'] || '',
          absentNote: getRowField(row, 'Jika pelajar tidak hadir'),
        };
      })
      .filter((row) => row.total > 0)
      .sort((a, b) => b.timestampMs - a.timestampMs)
      .slice(0, TEACHER_ATTENDANCE_SESSION_TARGET);

    const present = teacherRows.reduce((sum, row) => sum + row.present, 0);
    const total = teacherRows.reduce((sum, row) => sum + row.total, 0);
    const latest = teacherRows[0] || null;

    return {
      rows: teacherRows,
      aliases: teacherAttendanceAliases,
      present,
      total,
      latest,
      completedSessions: teacherRows.length,
      targetSessions: TEACHER_ATTENDANCE_SESSION_TARGET,
      rate: total ? Math.round((present / total) * 100) : null,
    };
  }, [attendanceRows, teacherAttendanceAliases, userData]);
  const teacherAttendanceRecordSet = useMemo(() => {
    const set = new Set();

    attendanceRows
      .filter((row) => isTeacherAttendanceRow(row, userData, teacherAttendanceAliases))
      .forEach((row) => {
        const date = parseSheetDate(row['Tarikh Sesi'] || row.Timestamp);
        const subject = row['Guru mata pelajaran'] || userData?.subject || '';
        const attendance = parseAttendanceFraction(row['Jumlah Kehadiran Pelajar (cnth: 23/25)']);

        if (date && subject && attendance.total > 0) {
          set.add(getAttendanceRecordKey(date, subject));
        }
      });

    return set;
  }, [attendanceRows, teacherAttendanceAliases, userData]);
  const attendanceRate = attendanceSummary.rate;
  const attendanceTone = getAttendanceTone(attendanceRate);
  const teacherAttendanceNeedsFollowUp = !loadingAttendance && (
    !attendanceSummary.total || (attendanceRate !== null && attendanceRate < 90)
  );
  const teacherAttendanceFollowUpUrl = useMemo(() => {
    if (!supportNumber || !teacherAttendanceNeedsFollowUp) return '';
    const teacherName = userData?.fullName || 'guru';
    const subject = userData?.subject || 'subjek';
    const status = attendanceSummary.total
      ? `${attendanceSummary.completedSessions}/${attendanceSummary.targetSessions} sesi direkod, kadar ${attendanceRate}%`
      : 'belum ada rekod kehadiran lengkap';
    const message = `Assalamualaikum BTOP, saya ${teacherName}. Saya ingin maklumkan status kehadiran ${subject}: ${status}.`;
    return `https://wa.me/${supportNumber}?text=${encodeURIComponent(message)}`;
  }, [attendanceRate, attendanceSummary, supportNumber, teacherAttendanceNeedsFollowUp, userData]);
  const completedClasses = useMemo(() => {
    const now = new Date();

    return schedules.filter((schedule) => {
      if (!schedule.date) return false;
      const time = schedule.endTime || schedule.startTime || '23:59';
      const sessionEnd = new Date(`${schedule.date}T${time}`);
      return sessionEnd < now;
    }).length;
  }, [schedules]);
  const pendingAttendanceSchedules = useMemo(() => {
    const now = new Date();

    return schedules
      .filter((schedule) => {
        if (!schedule.date) return false;
        const time = schedule.endTime || schedule.startTime || '23:59';
        const sessionEnd = new Date(`${schedule.date}T${time}`);
        if (Number.isNaN(sessionEnd.getTime()) || sessionEnd >= now) return false;

        return !teacherAttendanceRecordSet.has(getAttendanceRecordKey(schedule.date, schedule.subject || userData?.subject));
      })
      .sort((a, b) => {
        const dateCompare = (b.date || '').localeCompare(a.date || '');
        if (dateCompare !== 0) return dateCompare;
        return (a.startTime || '').localeCompare(b.startTime || '');
      });
  }, [schedules, teacherAttendanceRecordSet, userData?.subject]);
  const classTarget = Math.max(totalClasses, 10);
  const completedPercent = classTarget ? Math.min(100, Math.round((completedClasses / classTarget) * 100)) : 0;
  const classStatusTone = completedClasses >= 5 ? 'good' : completedClasses >= 3 ? 'warn' : 'bad';
  const classStatusLabel =
    completedClasses >= 5 ? 'Terkawal' : completedClasses >= 3 ? 'Perlu Semakan' : 'Kritikal';
  const wellnessMessage = todayClasses > 2
    ? 'Hari ini agak padat. Ambil masa sekejap untuk reset sebelum kelas seterusnya.'
    : todayClasses > 0
      ? 'Ritma hari ini nampak seimbang. Kekalkan tempo yang tenang untuk setiap sesi.'
      : 'Tiada kelas hari ini. Sesuai untuk menyemak bahan atau refleksi terdahulu.';

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          navigate('/login');
          return;
        }

        const snap = await getDoc(doc(db, 'users', user.uid));
        if (!snap.exists()) {
          navigate('/login');
          return;
        }

        const data = { uid: user.uid, ...snap.data() };
        if (data.role !== 'teacher') {
          navigate(`/dashboard/${data.role}`);
          return;
        }

        setUserData(data);
      } catch (error) {
        console.error('fetchUserData error:', error);
        showToast('bad', 'Ralat load user.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  useEffect(() => {
    const fetchSchedules = async () => {
      if (!userData?.daerah || !userData?.terminal) return;

      try {
        setLoadingSchedules(true);

        const schedulesRef = collection(db, 'schedules');
        const schedulesQuery = query(
          schedulesRef,
          where('daerah', '==', userData.daerah),
          where('terminal', '==', userData.terminal),
          where('active', '==', true)
        );

        const snap = await getDocs(schedulesQuery);
        const list = [];

        snap.forEach((docSnap) => {
          const data = docSnap.data();
          const subjectMatch =
            data.subject?.toLowerCase().includes(userData.subject?.toLowerCase()) ||
            data.teacher?.toLowerCase().includes(userData.fullName?.toLowerCase());

          if (subjectMatch) {
            list.push({
              id: docSnap.id,
              ...data
            });
          }
        });

        list.sort((a, b) => {
          const dateCompare = (a.date || '').localeCompare(b.date || '');
          if (dateCompare !== 0) return dateCompare;
          return (a.startTime || '').localeCompare(b.startTime || '');
        });

        setSchedules(list);
        setUpcomingSchedules(list.filter((schedule) => schedule.date && schedule.date >= todayISO).slice(0, 5));
      } catch (error) {
        console.error('fetchSchedules error:', error);
        showToast('bad', 'Ralat load jadual.');
      } finally {
        setLoadingSchedules(false);
      }
    };

    fetchSchedules();
  }, [todayISO, userData]);

  useEffect(() => {
    const fetchAttendanceRows = async () => {
      if (!userData) return;

      try {
        setLoadingAttendance(true);

        const res = await fetch(SHEET_CSV_URL);
        const text = await res.text();
        const parsed = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
        });

        if (parsed.errors?.length) {
          console.warn('Teacher attendance PapaParse warnings:', parsed.errors);
        }

        setAttendanceRows(parsed.data || []);
      } catch (error) {
        console.error('fetchAttendanceRows error:', error);
        showToast('bad', 'Gagal menyelaras data kehadiran Google Sheet.');
      } finally {
        setLoadingAttendance(false);
      }
    };

    fetchAttendanceRows();
  }, [userData]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('logout error:', error);
      showToast('bad', 'Logout gagal.');
    }
  };

  const goAllSchedules = () => {
    navigate('/dashboard/teacher/all-schedules');
  };

  const goSettings = () => {
    showToast('ok', 'Halaman tetapan belum siap lagi.');
  };

  const teacherNavItems = [
    { key: 'overview', label: 'Utama', onClick: () => setActiveTeacherView('overview') },
    { key: 'agenda', label: 'Agenda', onClick: () => setActiveTeacherView('agenda') },
    { key: 'attendance', label: 'Kehadiran', onClick: () => setActiveTeacherView('attendance') },
    { key: 'reflection', label: 'Refleksi', onClick: () => setActiveTeacherView('reflection') },
    { key: 'schedules', label: 'Jadual', onClick: goAllSchedules },
  ];

  if (loading) {
    return (
      <div className="teacher-dashboard-shell">
        <div className="teacher-loading">
          <div className="teacher-spinner" />
          <p>Memuatkan papan pemuka cikgu...</p>
        </div>
      </div>
    );
  }

  return (
    <DashboardShell
      brand="BTOP Wellness"
      subtitle="Papan Pemuka Cikgu"
      roleLabel="Cikgu"
      userName={userData?.fullName}
      userInitial={userData?.fullName?.charAt(0)?.toUpperCase() || 'T'}
      navItems={teacherNavItems}
      activeKey={activeTeacherView}
      onLogout={handleLogout}
      tone="green"
    >
    <div className="teacher-dashboard-shell">
      {toast.show && (
        <div className={`teacher-toast ${toast.type}`}>
          <span>{toast.msg}</span>
        </div>
      )}

      <header className="teacher-topbar">
        <div className="teacher-brand">
          <div className="teacher-brand-mark">
            <img src={bestLogo} alt="BEST BTOP logo" />
          </div>
          <div>
            <p className="teacher-brand-kicker">BTOP Wellness</p>
            <h1>Papan Pemuka Cikgu</h1>
          </div>
        </div>

        <div className="teacher-topbar-actions">
          <button className="teacher-pill-btn" type="button">
            <span className="teacher-pill-dot" />
            {todayClasses} kelas hari ini
          </button>
          <button className="teacher-avatar-btn" type="button" onClick={goSettings}>
            {userData?.fullName?.charAt(0)?.toUpperCase() || 'T'}
          </button>
          <button className="teacher-ghost-btn" type="button" onClick={handleLogout}>
            Keluar
          </button>
        </div>
      </header>

      <main className="teacher-dashboard-main">
        {activeTeacherView === 'overview' && (
        <>
        <section className="teacher-hero">
          <div className="teacher-hero-copy">
            <p className="teacher-eyebrow">{getGreeting()}</p>
            <h2>{firstName}, ruang kerja cikgu untuk hari ini.</h2>
            <p className="teacher-hero-text">{wellnessMessage}</p>

            <div className="teacher-context-row">
              <span>{userData?.subject || 'Subjek'}</span>
              <span>{userData?.daerah || '-'}</span>
              <span>{userData?.terminal || '-'}</span>
            </div>
          </div>

          <div className="teacher-hero-side">
            <div className={`teacher-focus-card class-status ${classStatusTone}`}>
              <div className="teacher-status-head">
                <p className="teacher-focus-label">Status Kelas</p>
                <span>{classStatusLabel}</span>
              </div>
              <div className="teacher-focus-value">
                {loadingSchedules ? '...' : `${completedClasses}/${classTarget}`}
              </div>
              <p className="teacher-focus-text">
                {completedClasses >= 5
                  ? 'Perkembangan kelas berada pada tahap baik.'
                  : completedClasses >= 3
                  ? 'Perkembangan sederhana. Semak kelas yang belum selesai.'
                  : 'Perkembangan masih rendah. Perlu perhatian dan tindakan susulan.'}
              </p>
              <div className="teacher-progress-track">
                <div style={{ width: `${completedPercent}%` }} />
              </div>
            </div>

            <div className="teacher-breathe-card">
              <p className="teacher-breathe-title">Sesi Seterusnya</p>
              {nextSchedule ? (
                <div className="teacher-next-session">
                  <span>{nextScheduleLabel}</span>
                  <strong>{nextSchedule.subject || 'Kelas seterusnya'}</strong>
                  <small>
                    {nextSchedule.schoolName || nextSchedule.daerah || '-'} | {nextSchedule.terminal || '-'}
                  </small>
                </div>
              ) : null}
              <p className="teacher-breathe-text">
                {nextSchedule
                  ? 'Semak bahan kelas dan sediakan nota refleksi selepas sesi tamat.'
                  : 'Belum ada sesi akan datang yang dijadualkan.'}
              </p>
              <button className="teacher-reflection-cta" type="button" onClick={() => setActiveTeacherView('reflection')}>
                Isi refleksi sesi
              </button>
            </div>
          </div>
        </section>

        <section className={`teacher-attendance-status ${attendanceTone}`}>
          <div>
            <p className="teacher-section-kicker">Kehadiran</p>
            <h3>{loadingAttendance ? 'Sedang selaras...' : getAttendanceLabel(attendanceRate)}</h3>
            <span>
              {loadingAttendance
                ? 'Sedang baca data Google Sheet.'
                : attendanceSummary.total
                ? `${attendanceSummary.completedSessions}/${attendanceSummary.targetSessions} sesi direkod | ${attendanceSummary.present}/${attendanceSummary.total} hadir.`
                : attendanceTone === 'good'
                ? 'Status baik: 90% ke atas.'
                : attendanceTone === 'warn'
                ? 'Perlu perhatian: 75% hingga 89%.'
                : attendanceTone === 'bad'
                ? 'Kritikal: bawah 75%.'
                : `Belum ada rekod kehadiran lengkap untuk ${attendanceSummary.aliases.join(' / ') || 'cikgu ini'} dalam Google Sheet.`}
            </span>
          </div>
          <div className="teacher-attendance-side">
            <small>
              {attendanceSummary.latest
                ? `Rekod terkini: ${attendanceSummary.latest.attendanceText}`
                : 'Sasaran 10 sesi untuk cikgu subjek ini'}
            </small>
            {teacherAttendanceFollowUpUrl && (
              <a href={teacherAttendanceFollowUpUrl} target="_blank" rel="noreferrer">
                WhatsApp BTOP
              </a>
            )}
          </div>
        </section>

        <section className={`teacher-pending-attendance ${pendingAttendanceSchedules.length ? 'needs-action' : 'complete'}`}>
          <div className="teacher-pending-head">
            <div>
              <p className="teacher-section-kicker">Perlu Rekod Kehadiran</p>
              <h3>
                {loadingAttendance || loadingSchedules
                  ? 'Sedang semak...'
                  : pendingAttendanceSchedules.length
                  ? `${pendingAttendanceSchedules.length} kelas belum direkod`
                  : 'Semua selesai'}
              </h3>
              <span>
                {loadingAttendance || loadingSchedules
                  ? 'Sistem sedang padankan jadual cikgu dengan rekod Google Sheet.'
                  : pendingAttendanceSchedules.length
                  ? 'Kelas ini sudah tamat tetapi belum ada rekod kehadiran dalam Google Sheet.'
                  : 'Semua kelas yang sudah tamat mempunyai rekod kehadiran.'}
              </span>
            </div>
            <button type="button" onClick={goAllSchedules}>
              Buka jadual
            </button>
          </div>

          {pendingAttendanceSchedules.length > 0 && (
            <div className="teacher-pending-list">
              {pendingAttendanceSchedules.slice(0, 4).map((schedule) => (
                <article key={`${schedule.id}-${schedule.date}`} className="teacher-pending-item">
                  <div>
                    <strong>{schedule.subject || userData?.subject || 'Subjek'}</strong>
                    <span>{formatMalayDate(schedule.date)}</span>
                    <small>
                      {formatTime(schedule.startTime)}
                      {schedule.endTime ? ` - ${formatTime(schedule.endTime)}` : ''} / {schedule.schoolName || schedule.daerah || '-'}
                    </small>
                  </div>
                  <em>Belum direkod</em>
                </article>
              ))}
            </div>
          )}

          {pendingAttendanceSchedules.length > 4 && (
            <p className="teacher-pending-more">+{pendingAttendanceSchedules.length - 4} kelas lagi dalam jadual penuh.</p>
          )}
        </section>

        <div className="teacher-attendance-legend">
          <span className="good">Hijau 90%+</span>
          <span className="warn">Kuning 75-89%</span>
          <span className="bad">Merah &lt;75%</span>
          <small>
            {loadingAttendance
              ? 'Sedang menyelaras Google Sheet.'
              : attendanceSummary.total
              ? 'Indicator ikut rekod kelas cikgu subjek ini.'
              : 'Rekod akan muncul selepas Google Sheet menerima data cikgu subjek ini.'}
          </small>
        </div>

        <section className="teacher-strip">
          <article>
            <span>Jumlah kelas aktif</span>
            <strong>{loadingSchedules ? '...' : totalClasses}</strong>
          </article>
          <article>
            <span>Kelas hari ini</span>
            <strong>{loadingSchedules ? '...' : todayClasses}</strong>
          </article>
          <article>
            <span>Akan datang</span>
            <strong>{loadingSchedules ? '...' : upcomingCount}</strong>
          </article>
          <article>
            <span>Kehadiran</span>
            <strong className={`teacher-attendance-value ${attendanceTone}`}>
              {loadingAttendance ? '...' : getAttendanceLabel(attendanceRate)}
            </strong>
          </article>
        </section>
        </>
        )}

        {activeTeacherView === 'agenda' && (
        <section className="teacher-agenda">
          <div className="teacher-section-head">
            <div>
              <p className="teacher-section-kicker">Agenda</p>
              <h3>Kelas akan datang</h3>
            </div>
            <button className="teacher-inline-link" type="button" onClick={goAllSchedules}>
              Buka semua jadual
            </button>
          </div>

          {loadingSchedules ? (
            <div className="teacher-empty-state">
              <div className="teacher-spinner small" />
              <p>Memuatkan jadual cikgu...</p>
            </div>
          ) : upcomingSchedules.length === 0 ? (
            <div className="teacher-empty-state">
              <h4>Belum ada kelas akan datang</h4>
              <p>Jadual seterusnya akan muncul di sini selepas pentadbir mengemaskini sesi baharu.</p>
            </div>
          ) : (
            <div className="teacher-schedule-list">
              {upcomingSchedules.map((schedule) => {
                const isToday = schedule.date === todayISO;

                return (
                  <article
                    key={schedule.id}
                    className={`teacher-schedule-item ${isToday ? 'today' : ''}`}
                  >
                    <div className="teacher-schedule-time">
                      <strong>{formatTime(schedule.startTime)}</strong>
                      <span>{schedule.endTime ? formatTime(schedule.endTime) : 'Masa belum ada'}</span>
                    </div>

                    <div className="teacher-schedule-body">
                      <div className="teacher-schedule-headline">
                        <h4>{schedule.subject || 'Subjek'}</h4>
                        <span className={`teacher-status ${isToday ? 'today' : 'soon'}`}>
                          {isToday ? 'Hari ini' : 'Akan datang'}
                        </span>
                      </div>

                      <p className="teacher-schedule-date">{formatMalayDate(schedule.date)}</p>

                      <div className="teacher-meta-row">
                        <span>{getTypeLabel(schedule.type)}</span>
                        <span>{schedule.daerah || '-'}</span>
                        <span>{schedule.terminal || '-'}</span>
                        {schedule.room ? <span>Bilik {schedule.room}</span> : null}
                      </div>

                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
        )}

        {activeTeacherView === 'reflection' && (
          <section className="teacher-agenda teacher-reflection-page">
            <div className="teacher-section-head">
              <div>
                <p className="teacher-section-kicker">Refleksi Guru</p>
                <h3>Catatan berstruktur selepas kelas</h3>
                <span>Jawab soalan ringkas tentang penguasaan, penglibatan, objektif pembelajaran dan cabaran kelas.</span>
              </div>
            </div>

            <div className="teacher-reflection-guide">
              <article><strong>1</strong><span>Pilih kelas dan tarikh sesi yang betul.</span></article>
              <article><strong>2</strong><span>Nilai penguasaan, penglibatan dan pencapaian objektif.</span></article>
              <article><strong>3</strong><span>Catat cabaran atau perubahan pelajar jika berkaitan.</span></article>
            </div>

            {TEACHER_REFLECTION_FORM_URL ? (
              <a className="teacher-reflection-open" href={TEACHER_REFLECTION_FORM_URL} target="_blank" rel="noreferrer">
                Buka Borang Refleksi Guru
              </a>
            ) : (
              <div className="teacher-reflection-unavailable">
                Pautan borang belum ditetapkan. Pentadbir perlu mengisi REACT_APP_TEACHER_REFLECTION_FORM_URL.
              </div>
            )}
          </section>
        )}

        {activeTeacherView === 'attendance' && (
        <>
        <section className="teacher-agenda teacher-attendance-page">
          <div className="teacher-section-head">
            <div>
              <p className="teacher-section-kicker">Kehadiran</p>
              <h3>Semakan rekod kehadiran</h3>
              <span>
                Paparan ini membantu cikgu semak kadar kehadiran dan kelas yang sudah tamat tetapi belum direkod.
              </span>
            </div>
            <button className="teacher-inline-link" type="button" onClick={goAllSchedules}>
              Buka jadual penuh
            </button>
          </div>

          <div className="teacher-attendance-kpis">
            <article>
              <span>Kadar kehadiran</span>
              <strong className={`teacher-attendance-value ${attendanceTone}`}>
                {loadingAttendance ? '...' : getAttendanceLabel(attendanceRate)}
              </strong>
            </article>
            <article>
              <span>Sesi direkod</span>
              <strong>{loadingAttendance ? '...' : `${attendanceSummary.completedSessions}/${attendanceSummary.targetSessions}`}</strong>
            </article>
            <article>
              <span>Belum direkod</span>
              <strong>{loadingAttendance || loadingSchedules ? '...' : pendingAttendanceSchedules.length}</strong>
            </article>
          </div>
        </section>

        <section className={`teacher-attendance-status ${attendanceTone}`}>
          <div>
            <p className="teacher-section-kicker">Status Semasa</p>
            <h3>{loadingAttendance ? 'Sedang selaras...' : getAttendanceLabel(attendanceRate)}</h3>
            <span>
              {loadingAttendance
                ? 'Sedang baca data Google Sheet.'
                : attendanceSummary.total
                ? `${attendanceSummary.completedSessions}/${attendanceSummary.targetSessions} sesi direkod | ${attendanceSummary.present}/${attendanceSummary.total} hadir.`
                : `Belum ada rekod kehadiran lengkap untuk ${attendanceSummary.aliases.join(' / ') || 'cikgu ini'} dalam Google Sheet.`}
            </span>
          </div>
          <div className="teacher-attendance-side">
            <small>
              {attendanceSummary.latest
                ? `Rekod terkini: ${attendanceSummary.latest.attendanceText}`
                : 'Sasaran 10 sesi untuk cikgu subjek ini'}
            </small>
            {teacherAttendanceFollowUpUrl && (
              <a href={teacherAttendanceFollowUpUrl} target="_blank" rel="noreferrer">
                WhatsApp BTOP
              </a>
            )}
          </div>
        </section>

        <section className={`teacher-pending-attendance ${pendingAttendanceSchedules.length ? 'needs-action' : 'complete'}`}>
          <div className="teacher-pending-head">
            <div>
              <p className="teacher-section-kicker">Perlu Rekod Kehadiran</p>
              <h3>
                {loadingAttendance || loadingSchedules
                  ? 'Sedang semak...'
                  : pendingAttendanceSchedules.length
                  ? `${pendingAttendanceSchedules.length} kelas belum direkod`
                  : 'Semua selesai'}
              </h3>
              <span>
                {loadingAttendance || loadingSchedules
                  ? 'Sistem sedang padankan jadual cikgu dengan rekod Google Sheet.'
                  : pendingAttendanceSchedules.length
                  ? 'Kelas ini sudah tamat tetapi belum ada rekod kehadiran dalam Google Sheet.'
                  : 'Semua kelas yang sudah tamat mempunyai rekod kehadiran.'}
              </span>
            </div>
            <button type="button" onClick={goAllSchedules}>
              Buka jadual
            </button>
          </div>

          {pendingAttendanceSchedules.length > 0 && (
            <div className="teacher-pending-list">
              {pendingAttendanceSchedules.slice(0, 8).map((schedule) => (
                <article key={`${schedule.id}-${schedule.date}`} className="teacher-pending-item">
                  <div>
                    <strong>{schedule.subject || userData?.subject || 'Subjek'}</strong>
                    <span>{formatMalayDate(schedule.date)}</span>
                    <small>
                      {formatTime(schedule.startTime)}
                      {schedule.endTime ? ` - ${formatTime(schedule.endTime)}` : ''} / {schedule.schoolName || schedule.daerah || '-'}
                    </small>
                  </div>
                  <em>Belum direkod</em>
                </article>
              ))}
            </div>
          )}
        </section>

        <div className="teacher-attendance-legend">
          <span className="good">Hijau 90%+</span>
          <span className="warn">Kuning 75-89%</span>
          <span className="bad">Merah &lt;75%</span>
          <small>Indicator ikut rekod kelas cikgu subjek ini.</small>
        </div>
        </>
        )}
      </main>
    </div>
    </DashboardShell>
  );
};

export default TeacherDashboard;
